<script setup lang="ts">
import { ChevronDown } from '@lucide/vue';
import SectionHeading from '@/components/marketing/shared/SectionHeading.vue';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';

const faqs = [
    {
        question: 'What does LaunchStation actually do?',
        answer: 'It learns about your business, writes and designs marketing content, suggests a posting plan, and helps you publish it across your channels. You stay in charge of what goes out.',
    },
    {
        question: 'Do I need to be technical?',
        answer: 'No. You give a website address, choose a goal, and talk to your assistant in normal language. There are no workflows to build and no plugins to install.',
    },
    {
        question: 'Which channels can it publish to?',
        answer: "The first connections cover the major social channels. What we can publish depends on each platform's own permissions and rules, so we label every connection as available or planned instead of overpromising.",
    },
    {
        question: 'Do I have to let it publish on its own?',
        answer: 'No. Review first is the default: your assistant prepares content and waits for your approval. You can also start in assist mode, where you publish everything yourself.',
    },
    {
        question: 'How does it learn about my business?',
        answer: 'It reads your website to understand your products, services and tone. You can correct anything it got wrong, and it remembers those corrections for next time.',
    },
    {
        question: 'When can I use the AI Video Studio?',
        answer: 'Video editing comes after the first release. The AI assistant, content generation, scheduling and approvals are the first things we are shipping.',
    },
];
</script>

<template>
    <section id="faq" class="scroll-mt-24 py-16 sm:py-20">
        <div class="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
                eyebrow="FAQ"
                title="Questions, answered."
                description="If something is still unclear, ask us at hello@launchstation.app."
            />

            <div class="border-border mt-10 border-t">
                <Collapsible
                    v-for="faq in faqs"
                    :key="faq.question"
                    class="border-border border-b"
                >
                    <CollapsibleTrigger
                        class="group flex w-full items-center justify-between gap-4 py-5 text-left"
                    >
                        <span class="font-display text-lg leading-snug">
                            {{ faq.question }}
                        </span>
                        <ChevronDown
                            class="text-muted-foreground size-5 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
                            aria-hidden="true"
                        />
                    </CollapsibleTrigger>
                    <CollapsibleContent
                        class="text-muted-foreground pb-5 text-sm leading-relaxed"
                    >
                        {{ faq.answer }}
                    </CollapsibleContent>
                </Collapsible>
            </div>
        </div>
    </section>
</template>
